class OmbrazUtils {
  static rootUrl = window.Shopify.routes.root;

  // Define all possible cart count selectors
  static CART_COUNT_SELECTORS = [
    ".cart-count-bubble span", // Old header
    ".sticky-header__cart-count", // New sticky header
    ".header-new__cart-count"
  ].join(", ");

  /**
   * Updates the button state to reflect various stages of the add-to-cart process.
   * @param {HTMLElement} button - The button element to update.
   * @param {string} state - The state to update the button to ('adding', 'added', 'error').
   */
  static updateAddToCartButtonState(button, state) {
    if (!button) return;
    switch (state) {
      case "adding":
        button.textContent = "Adding item...";
        button.disabled = true;
        button.style.opacity = "0.5";
        break;
      case "added":
        button.textContent = "Item added!";
        button.disabled = true;
        button.style.opacity = "0.5";
        setTimeout(() => {
          button.textContent = "Add to Cart";
          button.disabled = false;
          button.style.opacity = "1";
        }, 2000);
        break;
      case "error":
        button.textContent = "Error, try again";
        button.disabled = false;
        button.style.opacity = "1";
        break;
    }
  }

  /**
   * Adds multiple items to the cart and manages the state of the button during the process.
   * @param {Array} items - An array of items to add to the cart.
   * @param {Object} options - Options for the method.
   * @param {HTMLElement} [options.button=null] - The button element to update during the process (optional).
   * @param {boolean} [options.updateSlideCart=true] - Whether to trigger SLIDECART_UPDATE and SLIDECART_OPEN (optional).
   * @returns {Promise} - A promise that resolves with the response JSON data or rejects with an error.
   */
  static async v2AddToCart(items, options = {}) {
    const { button = null, updateSlideCart = true } = options;

    // Update button to 'adding' state if a button is provided
    if (button) this.updateAddToCartButtonState(button, "adding");

    try {
      const response = await fetch(OmbrazUtils.rootUrl + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (response.ok) {
        if (button) this.updateAddToCartButtonState(button, "added");

        // Update cart count
        await this.updateCartCount();

        // Optionally update the slide cart
        if (updateSlideCart) {
          OmbrazUtils.updateSlideCart({ isUpdate: true, isOpen: true });
        }

        return data;
      } else {
        if (button) this.updateAddToCartButtonState(button, "error");
        // Better error handling
        const errorMessage =
          data.description || data.message || "Error adding items to cart";
        console.error("Cart Error:", errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      if (button) this.updateAddToCartButtonState(button, "error");
      console.error("Cart Error:", error.message);
      throw error;
    }
  }

  /**
   * Adds a product to the cart, along with an optional side shield.
   * @param {string} productId - The ID of the product to add to the cart.
   * @param {string|null} sideShieldId - The ID of the side shield to add to the cart (optional).
   * @param {number} qty - The quantity of the product to add to the cart (default is 1).
   * @returns {Promise} - A promise that resolves with the response JSON data.
   */
  static async addToCart(productId, sideShieldId = null, qty = 1) {
    const items = [
      {
        id: productId,
        quantity: qty,
      },
      {
        id: sideShieldId,
        quantity: qty,
      },
    ];

    try {
      const response = await fetch(OmbrazUtils.rootUrl + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: items.filter((item) => item.id),
        }),
      });

      if (response.ok) {
        OmbrazUtils.updateCartCount();
        OmbrazUtils.updateSlideCart({ isUpdate: true, isOpen: true });
      }
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  }

  /**
   * Adds a side shield to the cart.
   * @param {string} sideShieldId - The ID of the side shield to add to the cart.
   * @param {number} qty - The quantity of the side shield to add to the cart (default is 1).
   * @returns {Promise} - A promise that resolves with the response JSON data.
   */
  static async addSideShield(sideShieldId, qty = 1) {
    try {
      const response = await fetch(OmbrazUtils.rootUrl + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: sideShieldId,
          quantity: qty,
        }),
      });

      return await response.json();
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  }

  /**
   * Removes a product and/or a side shield from the cart.
   * @param {string|null|undefined} sideShieldId - The ID of the product to remove from the cart.
   * @param {string} productId - The ID of the side shield  to remove from the cart (optional).
   * @returns {Promise} - A promise that resolves with the response JSON data.
   */
  static async removeSideShield(sideShieldId, productId = null) {
    try {
      const response = await fetch(OmbrazUtils.rootUrl + "cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          updates: {
            [productId]: 0,
            [sideShieldId]: 0,
          },
        }),
      });

      return await response.json();
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  }

  /**
   * Fetches the current state of the Shopify cart.
   * @returns {Promise} - A promise that resolves with the response JSON data.
   */
  static async fetchCart() {
    try {
      const response = await fetch(OmbrazUtils.rootUrl + "cart.js");

      return await response.json();
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  }

  /**
   * Clears the Shopify cart.
   * @returns {Promise} - A promise that resolves with the response JSON data.
   */
  static async clearCart() {
    try {
      const response = await fetch(OmbrazUtils.rootUrl + "cart/clear.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        OmbrazUtils.updateCartCount();
        OmbrazUtils.updateSlideCart({ isUpdate: true, isOpen: false });
      }
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  }

  /**
   * Updates the cart count displayed on all possible cart count elements
   * Supports both old header and new sticky header
   */
  static async updateCartCount() {
    const cartCountElements = document.querySelectorAll(
      this.CART_COUNT_SELECTORS
    );

    if (!cartCountElements.length) {
      console.debug("No cart count elements found");
      return;
    }

    try {
      const { item_count } = await OmbrazUtils.fetchCart();
      cartCountElements.forEach((elem) => {
        elem.textContent = item_count;
      });
    } catch (error) {
      console.error("Error updating cart count:", error);
    }
  }

  /**
   * Delays the execution of SLIDECART_UPDATE and SLIDECART_OPEN functions by the specified time.
   * @param {number} delayTime - The time to delay execution in milliseconds (default is 1000).
   * @returns {void}
   * @deprecated Use `updateSlideCart` method instead.
   */
  static async delayExecutionSlideCart(delayTime = 1000) {
    setTimeout(() => {
      SLIDECART_UPDATE();
      SLIDECART_OPEN();
    }, delayTime);
  }

  /**
   * Updates the slide cart by executing SLIDECART_UPDATE and SLIDECART_OPEN functions after a specified delay.
   * @param {Object} options - An object containing options for updating the slide cart.
   * @param {number} options.delayTime - The time to delay execution in milliseconds (default is 1000).
   * @param {boolean} options.isUpdate - Whether to execute SLIDECART_UPDATE function (default is false).
   * @param {boolean} options.isOpen - Whether to execute SLIDECART_OPEN function (default is false).
   * @returns {void}
   */
  static async updateSlideCart(
    options = {
      delayTime: 1000,
      isUpdate: false,
      isOpen: false,
    }
  ) {
    setTimeout(() => {
      if (options.isUpdate) SLIDECART_UPDATE();
      if (options.isOpen) SLIDECART_OPEN();
    }, options.delayTime);
  }

  /**
   * Map of screen resolutions to iPhone models.
   * @type {Object.<string, string>}
   * @private
   */
  static #iPhoneModels = {
    // Older models
    "414x896": "iPhone 11",

    // Newer models
    "393x852": "iPhone 15",
    "390x844":
      "iPhone 12, iPhone 13, iPhone 14, iPhone 13 Pro, iPhone 12 Pro, iPhone 15 Pro",
    "428x926":
      "iPhone 15 Plus, iPhone 15 Pro Max, iPhone 14 Pro Max, iPhone 13 Pro Max",
  };

  /**
   * Detects the specific iPhone model based on screen resolution.
   * @returns {string} The detected iPhone model or "Unknown iPhone model".
   */
  static detectiPhoneModel() {
    if (!/iPhone/.test(navigator.userAgent)) {
      return "Not an iPhone";
    }

    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const screenResolution = `${Math.min(screenWidth, screenHeight)}x${Math.max(
      screenWidth,
      screenHeight
    )}`;
    const actualResolution = `${Math.min(screenWidth, screenHeight) * window.devicePixelRatio
      }x${Math.max(screenWidth, screenHeight) * window.devicePixelRatio}`;

    return (
      this.#iPhoneModels[screenResolution] ||
      this.#iPhoneModels[actualResolution] ||
      "Unknown iPhone model"
    );
  }

  /**
   * Detects the device type based on the user agent string.
   * @returns {string} The detected device type.
   */
  static detectDevice() {
    const userAgent = navigator.userAgent.toLowerCase();
    const deviceTypes = [
      { name: "iOS", pattern: /iphone|ipad|ipod/ },
      { name: "Android", pattern: /android/ },
      { name: "Windows Phone", pattern: /windows phone/ },
      { name: "Windows PC", pattern: /win/ },
      { name: "Mac", pattern: /mac/ },
      { name: "Linux", pattern: /linux/ },
      { name: "BlackBerry", pattern: /blackberry|bb10|rim tablet os/ },
    ];

    for (const device of deviceTypes) {
      if (device.pattern.test(userAgent)) {
        return device.name;
      }
    }

    return "Unknown";
  }

  /**
   * Helper function to format money in cents to the user's locale currency.
   * @param {number} amount - The amount in cents to format.
   * @returns {string} The formatted amount in the user's locale
   * currency (e.g., "$10").
   */
  static formatMoneyInCents(amount) {
    const locale = window.Shopify.locale;
    const country = window.Shopify.country;
    const currency = window.Shopify.currency.active;
    const localeFormat = locale + "-" + country;

    return new Intl.NumberFormat(localeFormat, {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
    }).format(amount / 100);
  }

  /**
   *  Helper function to remove dollar sign and parse integer from a string.
   * @param {string} str - The string to remove the dollar sign from.
   * @returns {number} The parsed integer value.
   */
  static removeDollarSign(str) {
    return parseFloat(str.replace(/\D/g, ""));
  }

  /**
   * Stops the webcam stream associated with the given video element selector and optionally hides the VTO modal.
   *
   * This method checks for the presence of a video element with an active media stream
   * and stops all tracks (both video and audio) associated with that stream.
   * If a VTO (Virtual Try-On) modal is present, it hides the modal by setting its display to "none".
   *
   * By default, this method targets the first <video> element in the document, but a custom CSS selector
   * can be provided to target a specific video element.
   *
   * @param {string} [selector="video"] - The CSS selector for the video element to stop the webcam stream from.
   * @static
   */
  static stopWebcamFromVideoElement(selector = "video") {
    const videoElement = document.querySelector(selector);
    const vtoModal = document.querySelector(".arlab__webfitset__try__modal");

    // Hide VTO modal if it exists
    if (vtoModal) {
      vtoModal.style.display = "none";
    }

    // Stop the webcam stream if the video element has an active stream
    if (videoElement && videoElement.srcObject) {
      const stream = videoElement.srcObject;
      stream.getTracks().forEach((track) => track.stop());
    }
  }
}
